import useTwinTranslation from '../../../../../../../../../utils/hooks/useTwinTranslation'
import { VariantsPayload } from '../../../../../../../../../utils/reducers/products/variants'
import { ModalComplexProductOrderParsedData } from '../types'
import InputQuantityButtons, { SmallQuantityButton } from '../../../../../../../../../forms/Input/InputQuantityButtons'
import { useCallback } from 'react'
import { ModalComplexProductOrderTableConfig } from './types'
import TwinIcon from '../../../../../../../../../baseComponents/TwinIcon'
import { faMinus, faPlus } from '@fortawesome/pro-solid-svg-icons'
import { TwinDictionary } from '../../../../../../../../../utils/globals/dictionary'
import VirtualTableListing from '../../../../../../../../../baseComponents/TwinTable/VirtualTableListing'
import { ColumnTableSchema } from '../../../../../../../../../baseComponents/TwinTable/VirtualTableListing/Subcomponents/types'
import { RectangularInputPrice } from '../../../../../../../../../forms/Input/InputPrice'
import { ProductExtendedTyForComplexOrder } from '../../../../../../../../Product/types'
import TwinToolTip from '../../../../../../../../../baseComponents/TwinToolTip'

interface ModalComplexProductOrderTableProps extends ModalComplexProductOrderTableLogicProps {
    variants: VariantsPayload
    columns: number[]
    main: number
    config: ModalComplexProductOrderTableConfig
    productName: string
}

const ModalComplexProductOrderTable: React.FC<ModalComplexProductOrderTableProps> = ({ config, columns, variants, main, productData, setProductData, productName}) => {
    const mainVariant = variants?.variants[main]
    const {onChangeValue, onChangeColumn, onChangeAll, onChangeRow} = useModalComplexProductOrderTableLogic({productData, setProductData})
    const { t } = useTwinTranslation()
    let classNameCombinations = ''
    if (config.showReference) {
        classNameCombinations += ' mt-20'
    }
    if (config.showPrice) {
        classNameCombinations += ' mb-34'
    }
    const flexColumns: ColumnTableSchema<TwinDictionary> = [
        {
            id: 'combinationName',
            dataKey: 'combinationName',
            label: () => <Label className='justify-between w-full' onClick={onChangeAll}>{t('variant', 'Variante') + ' / ' + mainVariant?.name}</Label>,
            width: 200,
            className: 'mr-15',
            headerClassName: 'mr-15',
            customRender: (variant, allRowData) => {
                return <Label onClick={(increase) => onChangeRow(increase, allRowData.absoluteIndex || allRowData.rowIndex)} className={'justify-between w-full' + (classNameCombinations)}>{variant}</Label>
            },
        }
    ]
    let haveOther = false
    for (const column of columns) {
        if (column) {
            flexColumns.push({
                id: 'col_' + String(column),
                width: 185,
                dataKey: column,
                label: () => <HeaderLabel onClick={(increase) => onChangeColumn(increase, column)}>{mainVariant?.values?.[column].name || (columns.length === 1 ? productName : t('other', 'Otros'))}</HeaderLabel>,
                className: 'justify-center flex',
                customRender: (variant: any, allRowData) => {
                    return myCustomRender(variant, config, (value, key) => onChangeValue(value, key, allRowData.absoluteIndex || allRowData.rowIndex, column))
                }
            })
        } else {
            haveOther = true
        }
    }
    if (haveOther) {
        flexColumns.push({
            id: 'col_0',
            width: 150,
            dataKey: '0',
            label: () => <HeaderLabel onClick={(increase) => onChangeColumn(increase, 0)}>{columns.length === 1 ? productName : t('other', 'Otros')}</HeaderLabel>,
            className: 'justify-center flex',
            customRender: (variant: any, allRowData) => myCustomRender(variant, config, (value, key) => onChangeValue(value, key, allRowData.absoluteIndex || allRowData.rowIndex, 0))
        })
    }
    let rowHeight = 48
    if (config.showReference) {
        rowHeight += 13
    }
    if (config.showPrice) {
        rowHeight += 35
    }

    return (
        <div className='flex-auto'>
            <VirtualTableListing tableData={productData} columns={flexColumns} rowHeight={rowHeight} acceptHeightChanges={true} name='modalComplexProductOrderTable' disableMarginAuto={true} />
        </div>
    )
}

interface ModalComplexProductOrderTableLogicProps {
    productData: ModalComplexProductOrderParsedData | null
    setProductData: React.Dispatch<React.SetStateAction<ModalComplexProductOrderParsedData | null>>
}

const useModalComplexProductOrderTableLogic = ({ productData, setProductData }: ModalComplexProductOrderTableLogicProps) => {
    const onChangeValue = useCallback((value: string, key: keyof ProductExtendedTyForComplexOrder, rowIndex: number, column: number) => {
        setProductData((oldProductData) => {
            if (!oldProductData) {
                return null
            }
            const copy = JSON.parse(JSON.stringify(oldProductData))
            if (copy?.[rowIndex]?.[column]) {
                // @ts-ignore
                copy[rowIndex][column][key] = value
            }
            return  [...copy ]
        })
    }, [setProductData])

    const onChangeColumn = useCallback((increase: boolean, columnKey: number) => {
        setProductData((oldProductData) => {
            if (!oldProductData) {
                return null
            }
            const copy = JSON.parse(JSON.stringify(oldProductData))
            for (let row of copy) {
                const column = row[columnKey]
                if (typeof column === 'object') {
                    if (increase) {
                        column.quantity = (parseFloat(String(column.quantity || '0'))) + 1
                    } else if(column.quantity) {
                        column.quantity = (parseFloat(String(column.quantity || '0'))) - 1
                    }
                }
            }
            return [...copy ]
        })
    }, [setProductData])

    const onChangeAll = useCallback((increase: boolean) => {
        setProductData((oldProductData) => {
            if (!oldProductData) {
                return null
            }
            const copy = JSON.parse(JSON.stringify(oldProductData))
            for (let row of copy) {
                for (let columnKey in row) {
                    const column = row[columnKey]
                    if (typeof column === 'object') {
                        if (increase) {
                            column.quantity = (parseFloat(String(column.quantity || '0'))) + 1
                        } else if(column.quantity) {
                            column.quantity = (parseFloat(String(column.quantity || '0'))) - 1
                        }
                    }
                }   
            }
            return [...copy ]
        })
    }, [setProductData])

    const onChangeRow = useCallback((increase: boolean, rowKey: number) => {
        setProductData((oldProductData) => {
            if (!oldProductData) {
                return null
            }
            const copy = JSON.parse(JSON.stringify(oldProductData))
            const row = copy[rowKey]
            for (let columnKey in row) {
                const column = row[columnKey]
                if (typeof column === 'object') {
                    if (increase) {
                        column.quantity = (parseFloat(String(column.quantity || '0'))) + 1
                    } else if(column.quantity) {
                        column.quantity = (parseFloat(String(column.quantity || '0'))) - 1
                    }
                }
            }   
            return [...copy ]
        })
    }, [setProductData])

    return {onChangeValue, onChangeColumn, onChangeAll, onChangeRow}
}


interface LabelProps {
    onClick: (increase: boolean) => void
    children: React.ReactNode
    className?: string
}

const HeaderLabel: React.FC<LabelProps> = ({ onClick, children }) => {
    return (
        <Label onClick={onClick} className={'justify-center mx-auto w-full max-w-130'}>{children}</Label>
    )
}
const Label: React.FC<LabelProps> = ({ onClick, children, className }) => {
    const label = String(children)
    return (
        <div className={'items-center flex ' + (className || '')}><SmallQuantityButton onClick={() => onClick(false)}><TwinIcon icon={faMinus} fontSize={11} /></SmallQuantityButton>
            <TwinToolTip place='top' id={label} clickable className='max-w-300' content={label} />
            <span data-tooltip-id={label} className='text-ellipsis whitespace-nowrap overflow-hidden mx-6 flex-auto text-center'>{children}</span>
            <SmallQuantityButton onClick={() => onClick(true)}><TwinIcon icon={faPlus} fontSize={11} /></SmallQuantityButton></div>
    )
}


const myCustomRender = (variant: any, config: ModalComplexProductOrderTableConfig, onChange: (value: string, key: keyof ProductExtendedTyForComplexOrder) => void) => {
    const result: JSX.Element[] = []
    if (config?.showReference) {
        result.push(<div className='text-center text-12 mb-2'><span>{variant?.reference}</span></div>)
    }
    if (variant) {
        result.push(<InputQuantityButtons value={variant.quantity} onChange={(value) => onChange(value, 'quantity')} />)
    }
    if (config?.showPrice) {
        result.push(<RectangularInputPrice CurrencyId={config?.CurrencyId} value={variant?.initialUnitPriceWithoutTax || ''} className='w-130 mt-5' onChange={(value) => onChange(value, 'initialUnitPriceWithoutTax')} />)
    }
    return <div>{result}</div>
}

export default ModalComplexProductOrderTable